import { getAxiosObject } from '@/utils/library'
import {
  commonGetters,
  commonActions,
  commonMutations
} from '@/store/store_utils'

const dataName = 'owners'

const state = {
  owners: [],
  dataIndexedById: {},
  updatedAt: '20101010101010',
  acquiredPatientIds: new Set(),
  isSimpleSearch: true,
  searchWord: '',
  searchOwnerTypeId: 0,
  searchConditions: [{ id: 1, selectId: 0, text: '', text2: '' }],
  sortType: 0,
  orderType: 0
}

const getters = {
  ...commonGetters(
    dataName,
    ['getData', 'getDataIncludeDel', 'getDataById', 'getUpdatedAt'],
    { filterDel: true }
  ),
  getAcquiredPatientIds(state) {
    return state.acquiredPatientIds
  },
  getIsSimpleSearch(state) {
    return state.isSimpleSearch
  },
  getSearchWord(state) {
    return state.searchWord
  },
  getSearchOwnerTypeId(state) {
    return state.searchOwnerTypeId
  },
  getSearchConditions(state) {
    return state.searchConditions
  },
  getSortType(state) {
    return state.sortType
  },
  getOrderType(state) {
    return state.orderType
  }
}

const actions = {
  ...commonActions(['setData', 'setUpdatedAt', 'updateAfter']),
  async create({ commit }, { owner, reservation, mailFlgs }) {
    try {
      const axiosObject = getAxiosObject()
      const res = reservation
        ? await axiosObject.post('/owners', {
            owner,
            reservation,
            mailFlgs
          })
        : await axiosObject.post('/owners', {
            owner
          })
      if (res.status === 200) {
        commit('updateAfter', [res.data.owner])
        if (res.data.reservations) {
          commit('reservations/updateAfter', res.data.reservations, {
            root: true
          })
        }
        if (res.data.sendMail) {
          commit('sendMails/updateAfter', [res.data.sendMail], {
            root: true
          })
        }
        return { result: true, ownerId: res.data.owner.id }
      }
    } catch (error) {
      if (error.response?.data?.message === 'already used') {
        if (error.response?.data?.extra.field === 'showId') {
          commit('updateAfter', [error.response.data.extra.owner])
        }
        return `used ${error.response.data.extra.field}`
      } else if (error.response?.data?.message === 'no data') {
        return `no ${error.response.data.extra}`
      } else if (error.response?.data?.message) {
        if (error.response.data.extra?.data) {
          commit('reservations/setData', error.response.data.extra.data, {
            root: true
          })
        }
        return error.response.data.message
      } else {
        return 'server error'
      }
    }
  },
  async update({ commit }, { owner, updatePatients }) {
    try {
      const axiosObject = getAxiosObject()
      const res = await axiosObject.put('/owners', {
        owner,
        updatePatients
      })
      if (res.status === 200) {
        commit('updateAfter', [res.data.owner])
        if (res.data.patients.length !== 0) {
          commit('patients/updateAfter', res.data.patients, {
            root: true
          })
        }
        return true
      }
    } catch (error) {
      if (error.response?.data?.message === 'already used') {
        if (error.response?.data?.extra.field === 'showId') {
          commit('updateAfter', [error.response.data.extra.owner])
        }
        return `used ${error.response.data.extra.field}`
      } else if (error.response?.data?.message === 'no data') {
        return `no ${error.response.data.extra}`
      } else if (error.response?.data?.message) {
        if (error.response.data.extra?.data) {
          commit('setData', error.response.data.extra.data)
        }
        return error.response.data.message
      } else {
        return 'server error'
      }
    }
  },
  async delete({ commit, rootGetters }, id) {
    try {
      const axiosObject = getAxiosObject()
      const res = await axiosObject.delete('/owners', {
        data: { id }
      })
      commit('updateAfter', [res.data.owner])
      commit('patients/updateAfter', res.data.patients, { root: true })
      const karteFlg = rootGetters['auth/karteFlg']
      if (karteFlg) {
        const stores = [
          'anicomCIdChecks',
          'anicomPatients',
          'antibodies',
          'antibodyTypes',
          'estimateTreatmentItems',
          'estimates',
          'examinationPdfFiles',
          'examinationResults',
          'hospitalizations',
          'ipetChecks',
          'ipetPatients',
          'measurementResultItems',
          'measurementResults',
          'medicalContentImageTexts',
          'medicalContentImages',
          'medicalContents',
          'medicalPayments',
          'medicalRecords',
          'medicalTreatmentItems',
          'nxMeasurementResults',
          'patientVaccinePeriods',
          'patientVaccines',
          'payments',
          'problems',
          'reservationRequests',
          'reservations',
          'uploadImages',
          'vaccineReminders'
        ]
        stores.forEach(store => {
          const storeData = res.data[store]
          if (storeData.length > 0) {
            commit(`${store}/updateAfter`, storeData, { root: true })
          }
        })
      } else {
        if (res.data.anicomPatients.length > 0) {
          commit('anicomPatients/updateAfter', res.data.anicomPatients, {
            root: true
          })
        }
        if (res.data.ipetPatients.length > 0) {
          commit('ipetPatients/updateAfter', res.data.ipetPatients, {
            root: true
          })
        }
        if (res.data.reservations.length > 0) {
          commit('reservations/updateAfter', res.data.reservations, {
            root: true
          })
        }
        if (res.data.reservationRequests.length > 0) {
          commit(
            'reservationRequests/updateAfter',
            res.data.reservationRequests,
            { root: true }
          )
        }
      }
      return true
    } catch (error) {
      if (error.response?.data?.message === 'currently used') {
        return `used in ${error.response.data.extra}`
      } else if (error.response?.data?.message) {
        if (error.response.data.extra?.data) {
          commit('setData', error.response.data.extra.data)
        }
        return error.response.data.message
      } else {
        return 'server error'
      }
    }
  },
  async get({ commit, rootGetters }, { id, requiredPatientIds }) {
    try {
      const axiosObject = getAxiosObject()
      const res = await axiosObject.post(`/${dataName}/get`, {
        id,
        requiredPatientIds
      })
      if (res.status === 200) {
        commit('insertPatientIds', requiredPatientIds)
        if (res.data.patientsImages.length > 0) {
          commit(
            'patients/setImages',
            { patientsImages: res.data.patientsImages, ownerId: id },
            { root: true }
          )
        }
        if (res.data.reservations.length > 0) {
          commit('reservations/updateAfter', res.data.reservations, {
            root: true
          })
        }
        const karteFlg = rootGetters['auth/karteFlg']
        if (karteFlg) {
          const vaccinationStores = [
            'patientVaccines',
            'patientVaccinePeriods',
            'antibodies',
            'antibodyTypes'
          ]
          vaccinationStores.forEach(store => {
            const storeData = res.data[store]
            if (storeData.length > 0)
              commit(`${store}/updateAfter`, storeData, { root: true })
          })
        }
        return 'success'
      }
    } catch (error) {
      if (error.response?.data?.message) {
        return error.response.data.message === 'no data'
          ? `no ${error.response.data.extra}`
          : error.response.data.message
      } else {
        return 'server error'
      }
    }
  },
  async getIsPasswordConfigured({ commit }, ownerId) {
    try {
      const axiosObject = getAxiosObject()
      const res = await axiosObject.get('/owners/password', {
        params: { id: ownerId }
      })
      return {
        result: true,
        isPasswordConfigured: res.data.isPasswordConfigured
      }
    } catch (error) {
      return 'server error'
    }
  },
  async createAndSendPassword({ commit }, ownerId) {
    try {
      const axiosObject = getAxiosObject()
      await axiosObject.put('/owners/password', {
        id: ownerId
      })
      return true
    } catch (error) {
      if (error.response?.data?.message) {
        if (error.response.data.extra?.data) {
          commit('setData', error.response.data.extra.data)
        }
        return error.response.data.message
      } else {
        return 'server error'
      }
    }
  },
  saveIsSimpleSearch({ commit }, isSimpleSearch) {
    commit('setIsSimpleSearch', isSimpleSearch)
  },
  saveSearchWord({ commit }, word) {
    commit('setSearchWord', word)
  },
  saveSearchOwnerTypeId({ commit }, id) {
    commit('setSearchOwnerTypeId', id)
  },
  saveSearchConditions({ commit }, conditions) {
    commit('setSearchConditions', conditions)
  },
  saveSortType({ commit }, sortType) {
    commit('setSortType', sortType)
  },
  saveOrderType({ commit }, orderType) {
    commit('setOrderType', orderType)
  }
}

const mutations = {
  ...commonMutations(dataName, ['setData', 'setUpdatedAt', 'updateAfter'], {
    indexedById: true,
    csvColumns: ['ownerTagIds']
  }),
  insertPatientIds(state, patientIds) {
    patientIds.forEach(v => state.acquiredPatientIds.add(v))
  },
  setIsSimpleSearch(state, isSimpleSearch) {
    state.isSimpleSearch = isSimpleSearch
  },
  setSearchWord(state, word) {
    state.searchWord = word
  },
  setSearchOwnerTypeId(state, id) {
    state.searchOwnerTypeId = id
  },
  setSearchConditions(state, conditions) {
    state.searchConditions = conditions
  },
  setSortType(state, sortType) {
    state.sortType = sortType
  },
  setOrderType(state, orderType) {
    state.orderType = orderType
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
